import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import { Search, Package, CheckCircle2, Undo2, Download, Loader2, Pencil, Trash2, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import PurchaseBoardCreatePO from "./PurchaseBoardCreatePO";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function PurchaseBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = ["super_admin", "accounts", "admin"].includes(user?.role);
  // iter92 — Receive is now Store-only (Super Admin retained for oversight).
  // Removes the RECEIVE button from Accounts/Admin's Purchase Board view;
  // they can still see the row and download/edit/delete their own PO.
  const canReceive = ["super_admin", "store"].includes(user?.role);
  const canReopen = user?.role === "super_admin";
  // Iter78 — the user who created a PO can also edit / delete their own.
  const canManageOwn = (po) => user?.role === "super_admin"
    || (po && po.created_by === user?.id);

  const [data, setData] = useState(null);
  const [issuedStats, setIssuedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [board, dash] = await Promise.all([
        api.get(`/costing/purchase-board?status=${status}`),
        api.get("/costing/store-dashboard").catch(() => ({ data: null })),
      ]);
      setData(board.data);
      if (dash.data) setIssuedStats(dash.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const rows = data?.rows || [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.po_number, r.vendor_name, r.project_name, r.project_pid, r.created_by_name]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle))
    );
  }, [rows, q]);

  const markReceived = async (po) => {
    const notes = window.prompt(`Mark ${po.po_number} as received?\nOptional notes:`, "");
    if (notes === null) return;
    try {
      await api.post(`/costing/purchase-board/${po.id}/receive`, { notes: notes || null });
      toast.success(`${po.po_number} marked received`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reopen = async (po) => {
    if (!window.confirm(`Reopen ${po.po_number} (mark as pending again)?`)) return;
    try {
      await api.post(`/costing/purchase-board/${po.id}/reopen`, {});
      toast.success(`${po.po_number} reopened`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deletePO = async (po) => {
    if (!window.confirm(`Delete ${po.po_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/costing/project/${po.project_id}/purchase-orders/${po.id}`);
      toast.success(`${po.po_number} deleted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pdfUrl = (row) => `${api.defaults.baseURL}/costing/project/${row.project_id}/purchase-orders/${row.id}.pdf`;
  const totals = data?.totals || {};

  return (
    <DesktopLayout>
      <div className="max-w-[1600px] mx-auto p-6 space-y-4" data-testid="purchase-board-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="hk-tiny-label">Costing</div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900">Purchase Board</h1>
            <div className="text-xs text-slate-500 mt-1">
              Every open Purchase Order across all projects. Store confirms receipt when goods arrive.
            </div>
          </div>
          <button type="button" onClick={load} disabled={loading}
            className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="pb-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pb-totals">
          <Tile icon={Package} label="Pending POs" value={totals.pending_count || 0}
            sub={fmt(totals.pending_amount || 0)} tone="#c2410c"
            active={status === "pending"}
            onClick={() => { setStatus("pending"); document.getElementById("pb-table-anchor")?.scrollIntoView({ behavior: "smooth" }); }}
            testId="pb-tile-pending" />
          <Tile icon={CheckCircle2} label="Received POs" value={totals.received_count || 0}
            sub={fmt(totals.received_amount || 0)} tone="#15803d"
            active={status === "received"}
            onClick={() => { setStatus("received"); document.getElementById("pb-table-anchor")?.scrollIntoView({ behavior: "smooth" }); }}
            testId="pb-tile-received" />
          <Tile icon={Send} label="Issued Items"
            value={issuedStats?.issued_count ?? "…"}
            sub={issuedStats ? fmt(issuedStats.issued_value || 0) : "loading"}
            tone="#065f46"
            onClick={() => navigate("/store")}
            testId="pb-tile-issued" />
        </div>

        {canWrite && <PurchaseBoardCreatePO onCreated={load} />}

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input className="w-full border border-slate-300 rounded-sm pl-8 pr-3 py-2 text-sm"
              placeholder="Search PO number · vendor · project · creator"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="pb-search" />
          </div>
          <div className="inline-flex rounded-sm overflow-hidden border border-slate-300">
            {["pending", "received", "all"].map((s) => (
              <button key={s} type="button" onClick={() => setStatus(s)}
                data-testid={`pb-tab-${s}`}
                className={`px-3 py-2 text-xs font-bold uppercase ${status === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div id="pb-table-anchor" className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="pb-table">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr className="text-left">
                <Th>Status</Th>
                <Th>PO #</Th>
                <Th>Vendor</Th>
                <Th>Project</Th>
                <Th align="right">Amount</Th>
                <Th>Delivery</Th>
                <Th>Created</Th>
                <Th>Received</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400"
                  data-testid="pb-empty">
                  {status === "pending" ? "🎉 No pending purchase orders. Nothing to chase!" : "No purchase orders match."}
                </td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`pb-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-3 py-2 font-mono font-bold text-slate-900">
                      {r.po_number}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.vendor_name}</td>
                    <td className="px-3 py-2">
                      <Link to={`/projects/${r.project_id}`}
                        className="text-orange-700 hover:underline block max-w-[220px] truncate">
                        {r.project_name || "—"}
                      </Link>
                      <div className="font-mono text-[10px] text-slate-500 max-w-[220px] truncate">
                        {r.project_pid || ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                      {fmt(r.total)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {r.delivery_date || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-[10px]">
                      {r.created_by_name}<br />
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-[10px]">
                      {r.received_at ? (
                        <>
                          <div className="text-slate-700">{r.received_by_name}</div>
                          <div className="text-slate-400">{new Date(r.received_at).toLocaleDateString()}</div>
                          {r.received_notes && <div className="text-slate-500 italic">{r.received_notes}</div>}
                        </>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        <a href={pdfUrl(r)} target="_blank" rel="noopener noreferrer"
                          className="text-orange-700 hover:text-orange-900" title="Download PDF"
                          data-testid={`pb-pdf-${r.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </a>
                        {r.status === "pending" && canManageOwn(r) && (
                          <button type="button" onClick={() => setEditing(r)}
                            className="text-slate-600 hover:text-slate-900" title="Edit PO"
                            data-testid={`pb-edit-${r.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canManageOwn(r) && (
                          <button type="button" onClick={() => deletePO(r)}
                            className="text-red-600 hover:text-red-800" title="Delete PO"
                            data-testid={`pb-delete-${r.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {r.status === "pending" && canReceive && (
                          <button type="button" onClick={() => markReceived(r)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase"
                            data-testid={`pb-receive-${r.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Receive
                          </button>
                        )}
                        {r.status === "received" && canReopen && (
                          <button type="button" onClick={() => reopen(r)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-bold uppercase"
                            data-testid={`pb-reopen-${r.id}`}>
                            <Undo2 className="w-3 h-3" /> Reopen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-slate-400">
          Tip: Mark received once the vendor delivery is complete. Reopening is restricted to Super Admin.
        </div>
      </div>
      {editing && (
        <EditPOModal po={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </DesktopLayout>
  );
}

function EditPOModal({ po, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    vendor_id: po.vendor_id || "",
    delivery_date: po.delivery_date || "",
    notes: po.notes || "",
    lines: (po.lines && po.lines.length ? po.lines : [{}]).map((ln) => ({
      item_name: ln.item_name || "",
      quantity: ln.quantity ?? "",
      unit: ln.unit || "",
      unit_price: ln.unit_price ?? "",
      hsn: ln.hsn || "",
    })),
    extras: (po.extras || []).map((ex) => ({ label: ex.label || "", amount: ex.amount ?? "" })),
  }));
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/costing/vendors"); setVendors(data || []); }
      catch { /* noop */ }
    })();
  }, []);
  const setLine = (i, f, v) => setForm((F) => ({ ...F, lines: F.lines.map((ln, idx) => idx === i ? { ...ln, [f]: v } : ln) }));
  const addLine = () => setForm((F) => ({ ...F, lines: [...F.lines, { item_name: "", quantity: "", unit: "", unit_price: "", hsn: "" }] }));
  const rmLine = (i) => setForm((F) => ({ ...F, lines: F.lines.length > 1 ? F.lines.filter((_, idx) => idx !== i) : F.lines }));
  const setExtra = (i, f, v) => setForm((F) => ({ ...F, extras: F.extras.map((e, idx) => idx === i ? { ...e, [f]: v } : e) }));
  const addExtra = () => setForm((F) => ({ ...F, extras: [...F.extras, { label: "", amount: "" }] }));
  const rmExtra = (i) => setForm((F) => ({ ...F, extras: F.extras.filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor"); return; }
    const cleaned = form.lines
      .filter((ln) => (ln.item_name || "").trim() && Number(ln.quantity) > 0 && Number(ln.unit_price) >= 0)
      .map((ln) => ({
        item_name: ln.item_name.trim(),
        quantity: Number(ln.quantity),
        unit: ln.unit || null,
        unit_price: Number(ln.unit_price),
        hsn: ln.hsn || null,
      }));
    if (cleaned.length === 0) { toast.error("Add at least one valid line"); return; }
    const extras = (form.extras || [])
      .map((x) => ({ label: (x.label || "").trim(), amount: Number(x.amount) || 0 }))
      .filter((x) => x.label);
    setSaving(true);
    try {
      await api.patch(`/costing/project/${po.project_id}/purchase-orders/${po.id}`, {
        vendor_id: form.vendor_id,
        delivery_date: form.delivery_date || null,
        notes: form.notes || null,
        lines: cleaned, extras,
      });
      toast.success(`${po.po_number} updated`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const subtotal = form.lines.reduce((s, ln) => s + (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0), 0);
  const extrasTotal = form.extras.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const FIELD_ = "w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm";
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="pb-edit-modal">
      <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="hk-tiny-label">Edit PO</div>
            <h2 className="text-lg font-bold font-mono">{po.po_number}</h2>
          </div>
          <button type="button" onClick={onClose}
            className="text-slate-500 hover:text-slate-800" data-testid="pb-edit-close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="hk-tiny-label">Vendor</label>
            <select className={FIELD_} value={form.vendor_id}
              onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
              data-testid="pb-edit-vendor">
              <option value="">Select vendor…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="hk-tiny-label">Delivery date</label>
            <input type="date" className={FIELD_} value={form.delivery_date}
              onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
          </div>
          <div>
            <div className="hk-tiny-label mb-1">Lines</div>
            <div className="space-y-2">
              {form.lines.map((ln, i) => (
                <div key={i} className="border border-slate-100 rounded-sm p-2 space-y-1 bg-slate-50">
                  <input className={FIELD_} placeholder="Item name" value={ln.item_name}
                    onChange={(e) => setLine(i, "item_name", e.target.value)}
                    data-testid={`pb-edit-line-name-${i}`} />
                  <div className="grid grid-cols-4 gap-1">
                    <input className={FIELD_} type="number" placeholder="Qty" value={ln.quantity}
                      onChange={(e) => setLine(i, "quantity", e.target.value)} />
                    <input className={FIELD_} placeholder="Unit" value={ln.unit}
                      onChange={(e) => setLine(i, "unit", e.target.value)} />
                    <input className={FIELD_} type="number" step="0.01" placeholder="Rate" value={ln.unit_price}
                      onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                    <input className={FIELD_} placeholder="HSN" value={ln.hsn}
                      onChange={(e) => setLine(i, "hsn", e.target.value)} />
                  </div>
                  {form.lines.length > 1 && (
                    <button type="button" onClick={() => rmLine(i)}
                      className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addLine}
              className="mt-1 text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900">
              + Add line
            </button>
          </div>
          <div>
            <div className="hk-tiny-label mb-1">Extras (tax / transport / discount as −ve)</div>
            {form.extras.map((ex, i) => (
              <div key={i} className="flex items-center gap-1 mb-1">
                <input className={FIELD_ + " flex-1"} placeholder="Label" value={ex.label}
                  onChange={(e) => setExtra(i, "label", e.target.value)} />
                <input className={FIELD_ + " w-28"} type="number" step="0.01" placeholder="Amount"
                  value={ex.amount}
                  onChange={(e) => setExtra(i, "amount", e.target.value)} />
                <button type="button" onClick={() => rmExtra(i)}
                  className="text-red-600 hover:text-red-800 shrink-0 px-1">✕</button>
              </div>
            ))}
            <button type="button" onClick={addExtra}
              className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900">
              + Add extra
            </button>
          </div>
          <textarea className={FIELD_} rows={2} placeholder="Notes" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="text-right text-xs">
            <div className="text-slate-600">Subtotal: {fmt(subtotal)}</div>
            {form.extras.length > 0 && <div className="text-slate-600">Extras: {fmt(extrasTotal)}</div>}
            <div className="font-bold text-slate-900">Total: {fmt(subtotal + extrasTotal)}</div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={save} disabled={saving}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold rounded-sm text-sm"
            data-testid="pb-edit-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const tone = status === "received"
    ? { bg: "#dcfce7", color: "#15803d" }
    : { bg: "#fef3c7", color: "#a16207" };
  return (
    <span className="inline-flex px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase"
      style={{ background: tone.bg, color: tone.color }}
      data-testid={`pb-status-${status}`}>
      {status}
    </span>
  );
}

function Tile({ icon: Icon, label, value, sub, tone, onClick, active, testId }) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`bg-white border rounded-sm p-4 text-left transition ${
        onClick ? "cursor-pointer hover:border-slate-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-300" : ""
      } ${active ? "ring-2" : "border-slate-200"}`}
      style={active && tone ? { borderColor: tone, boxShadow: `0 0 0 2px ${tone}22` } : {}}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" style={tone ? { color: tone } : {}} />}
        <div className="hk-tiny-label">{label}</div>
      </div>
      <div className="text-2xl font-black tracking-tight mt-1"
        style={tone ? { color: tone } : {}}>
        {value}
      </div>
      {sub && <div className="text-[10px] font-mono text-slate-500 mt-0.5">{sub}</div>}
    </Wrapper>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className={`px-3 py-2 font-bold uppercase text-[10px] tracking-wide text-slate-500 ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}
